import React from "react";
import { Link } from "react-router-dom";
import { Shield, ExternalLink, Heart, Mail } from "lucide-react";
import InstallAppButton from "./InstallAppButton";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer
      className="border-t border-zinc-900 bg-white mt-24"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <img
              src="/icon.svg"
              alt="Paws on Record"
              width="40"
              height="40"
              className="border border-zinc-900"
            />
            <div>
              <div className="font-display font-black text-lg tracking-tight">
                PAWS ON RECORD
              </div>
              <div className="label-eyebrow">
                A public-interest transparency tool
              </div>
            </div>
          </div>
          <p className="mt-6 text-sm text-zinc-600 max-w-md leading-relaxed">
            A public-interest registry aggregating animal cruelty convictions
            across the United States. Records are compiled from official state
            and county registries (TN TBI, Henrico County VA, Suffolk County NY)
            and publicly reported news convictions — each with a verifiable
            source.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 border border-zinc-900 bg-zinc-100 px-3 py-2 text-[11px] uppercase tracking-widest text-zinc-800 font-bold">
            Public records · Not an official government source
          </div>
          <div className="mt-6">
            <InstallAppButton variant="prominent" />
            <p className="text-[11px] uppercase tracking-widest font-mono text-zinc-500 mt-2">
              Works offline · No App Store needed
            </p>
          </div>
        </div>

        <div>
          <div className="label-eyebrow mb-4">Explore</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="hover:underline">Registry</Link></li>
            <li><Link to="/report" className="hover:underline">Report Abuse</Link></li>
            <li><Link to="/nearby" className="hover:underline">Find Nearest Help</Link></li>
            <li><Link to="/about" className="hover:underline">About & Disclaimer</Link></li>
            <li><Link to="/partnerships" className="hover:underline">Partnerships</Link></li>
            <li><Link to="/sponsors" className="hover:underline" data-testid="footer-sponsors-link">Donors Wall</Link></li>
            <li><Link to="/in-memory" className="hover:underline" data-testid="footer-memorial-link">In Memory Of · Memorial Wall</Link></li>
            <li><Link to="/legal" className="hover:underline" data-testid="footer-legal-link">Terms, Privacy & Takedowns</Link></li>
            <li><Link to="/editorial" className="hover:underline" data-testid="footer-editorial-link">Editorial Standards</Link></li>
            <li><Link to="/where-your-money-goes" className="hover:underline" data-testid="footer-transparency-link">Where Your Money Goes</Link></li>
          </ul>
        </div>

        <div>
          <div className="label-eyebrow mb-4">Take Action</div>
          <ul className="space-y-2 text-sm">
            <li>
              <Link
                to="/donate"
                className="inline-flex items-center gap-1 hover:underline font-semibold"
                data-testid="footer-donate-link"
              >
                <Heart size={13} /> Donate to Allies
              </Link>
            </li>
            <li>
              <a
                href="https://www.humanesociety.org/resources/report-animal-cruelty"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:underline"
              >
                HSUS Report Cruelty <ExternalLink size={12} />
              </a>
            </li>
            <li>
              <a
                href="https://www.aspca.org/pet-care/report-animal-cruelty"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 hover:underline"
              >
                ASPCA Report <ExternalLink size={12} />
              </a>
            </li>
          </ul>
        </div>

        <div>
          <div className="label-eyebrow mb-4">Contact & Emergency</div>
          <ul className="space-y-2 text-sm">
            <li>
              <a
                href="mailto:info@pawsonrecord.com"
                className="inline-flex items-center gap-1.5 hover:underline font-semibold text-red-600"
                data-testid="footer-contact-email"
              >
                <Mail size={13} /> info@pawsonrecord.com
              </a>
            </li>
            <li className="text-xs text-zinc-600 pt-1">
              For corrections, takedown requests, partnership inquiries, or press.
            </li>
            <li className="pt-3 border-t border-zinc-200"><span className="font-semibold">Life-threatening:</span> 911</li>
            <li className="text-xs text-zinc-600">
              For non-emergencies contact your local Animal Control or county
              sheriff's office.
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-center text-[11px] uppercase tracking-widest text-zinc-600 font-mono">
          <div data-testid="footer-copyright">
            © {year} Refuel LLC dba Paws on Record. All rights reserved.
            <span className="block sm:inline sm:ml-3 mt-1 sm:mt-0 text-zinc-500">
              Donations keep this registry free —{" "}
              <Link to="/donate" className="underline hover:text-red-500" data-testid="footer-donate-link">
                support the cause
              </Link>
              .
            </span>
          </div>
          <div className="md:text-center text-[11px] normal-case tracking-normal font-sans">
            Source records are property of their respective issuing agencies.
            Third-party trademarks belong to their owners.
          </div>
          <div className="md:text-right">
            v1.2 · Public-interest project
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-6 text-[11px] text-zinc-500 leading-relaxed font-sans">
          This site is provided for informational purposes. Paws on Record is
          not affiliated with any government agency. Use of the data does not
          constitute a legal background check. Before making any legal,
          employment, housing, or adoption decision, verify the record with its
          authoritative source linked on each entry. Defamation, harassment,
          vigilantism, and unlawful discrimination are strictly prohibited —
          see our <Link to="/legal" className="underline">Legal</Link> page for
          the full terms.
        </div>
      </div>
    </footer>
  );
}
